import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material';
import {
  Crop as CropIcon,
  Delete as DeleteIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon
} from '@mui/icons-material';

interface AvatarEditorProps {
  open: boolean;
  onClose: () => void;
  currentAvatar?: string;
  subjectName?: string;
  title?: string;
  onSave: (base64: string | null) => void;
}

const AVATAR_SIZE = 200;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'P';
  const first = parts[0]?.charAt(0) || '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) || '' : '';
  return `${first}${last}`.toUpperCase();
}

export const AvatarEditor: React.FC<AvatarEditorProps> = ({
  open,
  onClose,
  currentAvatar,
  subjectName = '',
  title = 'Editar avatar',
  onSave
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(currentAvatar || null);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = AVATAR_SIZE;
    canvas.height = AVATAR_SIZE;

    ctx.save();
    ctx.clearRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);
    ctx.beginPath();
    ctx.arc(AVATAR_SIZE / 2, AVATAR_SIZE / 2, AVATAR_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);

    ctx.translate(AVATAR_SIZE / 2, AVATAR_SIZE / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(scale, scale);
    ctx.translate(-AVATAR_SIZE / 2 + position.x, -AVATAR_SIZE / 2 + position.y);

    const aspect = img.width / img.height;
    const drawWidth = aspect > 1 ? AVATAR_SIZE * aspect : AVATAR_SIZE;
    const drawHeight = aspect > 1 ? AVATAR_SIZE : AVATAR_SIZE / aspect;
    ctx.drawImage(
      img,
      (AVATAR_SIZE - drawWidth) / 2,
      (AVATAR_SIZE - drawHeight) / 2,
      drawWidth,
      drawHeight
    );
    ctx.restore();
  }, [position.x, position.y, rotation, scale]);

  useEffect(() => {
    if (imageSrc && imageRef.current && canvasRef.current) {
      requestAnimationFrame(drawCanvas);
    }
  }, [drawCanvas, imageSrc]);

  useEffect(() => {
    if (!open) {
      imageRef.current = null;
      return;
    }

    const validAvatar = currentAvatar && currentAvatar.trim() ? currentAvatar : null;
    setImageSrc(validAvatar);
    setRotation(0);
    setScale(1);
    setPosition({ x: 0, y: 0 });

    if (!validAvatar) {
      imageRef.current = null;
      return;
    }

    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      requestAnimationFrame(drawCanvas);
    };
    img.onerror = () => {
      imageRef.current = null;
      setImageSrc(null);
    };
    img.src = validAvatar;
    // Este efeito só deve inicializar o editor quando o modal abre ou quando o avatar muda.
    // Se depender de drawCanvas, cada mudança de zoom/rotação recria a função e reseta a imagem.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAvatar, open]);

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Selecione apenas arquivos de imagem.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 2MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = event => {
      const result = String(event.target?.result || '');
      const img = new Image();
      img.onload = () => {
        imageRef.current = img;
        setImageSrc(result);
        setRotation(0);
        setScale(1);
        setPosition({ x: 0, y: 0 });
        requestAnimationFrame(drawCanvas);
      };
      img.onerror = () => {
        alert('Não foi possível carregar a imagem selecionada.');
      };
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFileSelect(file);
    event.target.value = '';
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!imageSrc) return;
    setIsDragging(true);
    setDragStart({ x: event.clientX, y: event.clientY });
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !imageSrc) return;
    const dx = event.clientX - dragStart.x;
    const dy = event.clientY - dragStart.y;
    setPosition(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    setDragStart({ x: event.clientX, y: event.clientY });
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imageSrc) {
      onSave(null);
      onClose();
      return;
    }
    onSave(canvas.toDataURL('image/png'));
    onClose();
  };

  const handleRemove = () => {
    setImageSrc(null);
    imageRef.current = null;
    onSave(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                borderRadius: '50%',
                overflow: 'hidden',
                border: '3px solid',
                borderColor: 'divider',
                bgcolor: imageSrc ? 'transparent' : 'grey.100',
                cursor: imageSrc ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => {
                if (!imageSrc) fileInputRef.current?.click();
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
            >
              {imageSrc ? (
                <canvas
                  ref={canvasRef}
                  width={AVATAR_SIZE}
                  height={AVATAR_SIZE}
                  style={{ display: 'block', width: '100%', height: '100%' }}
                />
              ) : (
                <Typography variant="h3" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  {initials(subjectName)}
                </Typography>
              )}
            </Box>
            {imageSrc && (
              <IconButton
                onClick={handleRemove}
                size="small"
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'error.main',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'error.dark' }
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          <Button
            variant={imageSrc ? 'outlined' : 'contained'}
            component="label"
            startIcon={<CropIcon />}
          >
            {imageSrc ? 'Trocar imagem' : 'Selecionar imagem'}
            <input type="file" accept="image/*" hidden onChange={handleFileChange} />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileChange}
          />

          {imageSrc && (
            <Box sx={{ width: '100%' }}>
              <Typography gutterBottom>Rotação</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RotateLeftIcon />}
                  onClick={() => setRotation(prev => prev - 90)}
                >
                  Esquerda
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RotateRightIcon />}
                  onClick={() => setRotation(prev => prev + 90)}
                >
                  Direita
                </Button>
              </Box>
              <Typography gutterBottom>Zoom: {scale.toFixed(1)}x</Typography>
              <Box
                component="input"
                type="range"
                min={0.5}
                max={5}
                step={0.1}
                value={scale}
                onChange={event => setScale(Number(event.target.value))}
                onMouseDown={event => event.stopPropagation()}
                onTouchStart={event => event.stopPropagation()}
                sx={{ width: '100%' }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center' }}
              >
                Arraste a imagem no círculo para ajustar o enquadramento.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        {imageSrc && (
          <Button color="error" variant="outlined" onClick={handleRemove}>
            Remover
          </Button>
        )}
        <Button variant="contained" onClick={handleSave}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarEditor;
